import { byScheduledTime } from '@/utils/scheduleTime'

/**
 * Paperwork lookup for the delivery board: find a delivery by the number
 * printed on the document in someone's hand.
 *
 * Deliberately not a value filter like carrier or vendor. Those are
 * *categories* — a dozen values across hundreds of rows, where scrolling the
 * list is how you discover an option exists. A BOL or PO is an *identifier*,
 * roughly one per delivery, and opaque: nobody recognises `BL-1142-0184` by
 * sight. So the list here exists to prove a search is narrowing, not to be
 * browsed, and typing is the half of the interaction that does the work.
 *
 * The thresholds live in this module rather than the component because they
 * are policy, not layout — and policy is cheaper to pin in a unit test than
 * through a popover.
 */

const NON_ALPHANUMERIC = /[^a-z0-9]/gi

/**
 * Comparison form for a reference number.
 *
 * Printed paperwork numbers carry hyphens and spaces nobody retypes
 * faithfully — `1142-54101491-004` gets typed as `54101491`, or with spaces
 * for the dashes — so both sides drop punctuation and case before comparing.
 *
 * This is not fuzzy repair. A mistyped *digit* (the BOL survey's `146-` for
 * `1146-`) still misses, and fixing that belongs with the extraction
 * pipeline's matcher, which has the document and the confidence scores. A
 * filter box guessing at typos would silently show the wrong truck.
 */
export function normaliseRef(value: string): string {
  return value.replace(NON_ALPHANUMERIC, '').toUpperCase()
}

/** The fields a lookup reads. Structural, so tests can use bare objects. */
export type PaperworkFields = {
  bolNumber?: string
  bpoNumber: string
  scheduledFor: string
}

export type RefKind = 'BOL' | 'PO'

export type PaperworkCandidate<T> = {
  delivery: T
  /** The matched number, verbatim as printed — what selecting this applies. */
  ref: string
  kind: RefKind
  /** The query is this number outright, punctuation aside. */
  exact: boolean
}

export type PaperworkLimits = {
  /** Rows to show while there are more matches than a person can scan. */
  peek: number
  /** At or below this many matches, render all of them. */
  listAllAt: number
}

export type PaperworkResult<T> = {
  /** What the panel should render — already cut to the limits. */
  candidates: PaperworkCandidate<T>[]
  /** Matches before cutting, so the panel can say what it is not showing. */
  total: number
  /** `candidates` is a sample, not the whole answer. */
  truncated: boolean
}

function matchField(
  value: string | undefined,
  needle: string,
  kind: RefKind,
): { ref: string; kind: RefKind; exact: boolean } | null {
  if (!value) return null
  const normalised = normaliseRef(value)
  if (!normalised.includes(needle)) return null
  return { ref: value, kind, exact: normalised === needle }
}

/**
 * Exact matches first, then chronological.
 *
 * Chronological rather than alphabetical because the numbers carry nothing to
 * scan by, while "soonest first" matches why someone is looking: the truck is
 * expected, and that is what sent them hunting. (The known exception — an
 * office user pulling up an arbitrary delivery for information — is
 * QUESTIONS.md Q13.)
 *
 * The exact hoist is for the person typing or scanning a whole number. Their
 * row is *the answer*, not one entry among twenty that merely contain the
 * digits, so it must not sort below them. It also lands first, which is the
 * row cmdk pre-selects — so Enter takes it without a keystroke of navigation.
 */
function compareCandidates<T extends PaperworkFields>(
  a: PaperworkCandidate<T>,
  b: PaperworkCandidate<T>,
): number {
  if (a.exact !== b.exact) return a.exact ? -1 : 1
  return byScheduledTime(a.delivery, b.delivery)
}

/**
 * Deliveries whose BOL or PO contains `query`.
 *
 * An empty query matches nothing rather than everything: an unfiltered list of
 * opaque numbers is not browsable at any length, so the panel prompts instead.
 */
export function searchPaperwork<T extends PaperworkFields>(
  deliveries: readonly T[],
  query: string,
  limits: PaperworkLimits,
): PaperworkResult<T> {
  const needle = normaliseRef(query)
  if (!needle) return { candidates: [], total: 0, truncated: false }

  const matches: PaperworkCandidate<T>[] = []

  for (const delivery of deliveries) {
    // BOL first: it is the document the driver actually hands over, so when a
    // query hits both numbers that is the one to report back.
    const hit =
      matchField(delivery.bolNumber, needle, 'BOL') ??
      matchField(delivery.bpoNumber, needle, 'PO')

    // One row per delivery, never one per number. A delivery carries two, and
    // listing it twice would inflate a count whose whole job is to be trusted.
    if (hit) matches.push({ delivery, ...hit })
  }

  matches.sort(compareCandidates)

  const truncated = matches.length > limits.listAllAt
  return {
    candidates: matches.slice(0, truncated ? limits.peek : matches.length),
    total: matches.length,
    truncated,
  }
}

/**
 * Does this delivery carry the applied reference on either document?
 *
 * Normalised on both sides so a hand-edited or shared `?ref=BL11420177` finds
 * the same row as the one the picker wrote.
 */
export function matchesRef(delivery: PaperworkFields, ref: string): boolean {
  const needle = normaliseRef(ref)
  if (!needle) return true
  return (
    (delivery.bolNumber !== undefined &&
      normaliseRef(delivery.bolNumber) === needle) ||
    normaliseRef(delivery.bpoNumber) === needle
  )
}
